import { Box, EmployerLogo } from "@braintrust/braintrust-ui-components";
import { BookmarkJobButton } from "@js/apps/bookmarks/components";
import { useCtaClickedContext } from "@js/apps/common/context";
import { useAccountType, useUser } from "@js/apps/common/hooks";
import { ApplictionStatus } from "@js/apps/jobs/components/application-status";
import { JobBudgetWithPaymentTypeTooltip } from "@js/apps/jobs/components/job-budget-with-payment-type-tooltip";
import { JobForYouBasicInfo } from "@js/apps/jobs/components/job-for-you-basic-info";
import { JobTypeTagWithTooltip } from "@js/apps/jobs/components/job-type-tag-with-tooltip";
import { ShareJobButton } from "@js/apps/jobs/components/share-job-button";
import {
  DEFAULT_LISTING_BG_COLOR,
  FALLBACK_BG_COLOR,
} from "@js/apps/jobs/constants";
import { RouterLink } from "@js/components/link";
import type { SimilarJob } from "@js/types/jobs";

import { EmployerNameAndJobTitle } from "../employer-name-and-job-title";

import styles from "./styles.module.scss";

const getStatus = (
  job: SimilarJob,
): EnumType<typeof ENUMS.JobFreelancerStatus> | null => {
  if (job.invited_by_client) {
    return ENUMS.JobFreelancerStatus.INVITED_BY_CLIENT;
  }

  if (job.invited_by_matcher) {
    return ENUMS.JobFreelancerStatus.INVITED_BY_MATCHER;
  }

  return null;
};

export const JobForYouCarouselItem = ({ job }: { job: SimilarJob }) => {
  const { ctaClicked } = useCtaClickedContext();
  const { isFreelancer } = useAccountType();
  const user = useUser();

  const jobStatusToDisplay = getStatus(job);

  const { logo_background_color, logo_thumbnail } = job.employer;
  const bgColor = logo_thumbnail
    ? `var(${logo_background_color || DEFAULT_LISTING_BG_COLOR})`
    : FALLBACK_BG_COLOR;
  return (
    <Box className={styles.jobForYouCarouselItem} tabIndex={0}>
      <Box className={styles.jobForYouCarouselItemHeader}>
        <EmployerLogo
          className={styles.jobForYouCarouselItemLogo}
          src={{ logo_thumbnail, name: job.employer.name }}
          fallbackTextColor="dark-violet"
          bgColor={bgColor}
          fallbackTextSize="medium"
        />
        <Box className={styles.jobForYouCarouselItemActions}>
          <Box sx={{ display: "flex" }}>
            <JobTypeTagWithTooltip jobType={job.job_type} />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "fit-content",
              }}
            >
              {isFreelancer && (
                <ShareJobButton
                  job={job}
                  className={styles.jobForYouCarouselItemShare}
                />
              )}
              {isFreelancer && (
                <BookmarkJobButton
                  disabled={!user?.is_verified}
                  job={job}
                  className={styles.jobForYouCarouselItemSaveJob}
                />
              )}
            </Box>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            {!!jobStatusToDisplay && (
              <ApplictionStatus status={jobStatusToDisplay} />
            )}
          </Box>
        </Box>
      </Box>

      <Box className={styles.jobForYouCarouselItemContent}>
        <Box>
          <EmployerNameAndJobTitle job={job} />
        </Box>

        <Box>
          <JobBudgetWithPaymentTypeTooltip
            job={job}
            variant="title"
            size="small"
            fontWeight={400}
          />
        </Box>

        <Box>
          <JobForYouBasicInfo job={job} />
        </Box>
      </Box>

      <RouterLink
        to={`/jobs/${job.id}`}
        onClick={() => {
          ctaClicked(ENUMS.UserClicksEventCTAName.VIEW_JOB);
        }}
      >
        <Box className={styles.jobForYouCarouselItemLink} />
      </RouterLink>
    </Box>
  );
};
